'use client';

import { Loader2 } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useUpdateNotificationPreference } from '@/lib/api/notifications/hooks';
import { usePriceAlertCurrentValue } from '@/lib/api/priceAlerts';
import { DialogWalletStepUp } from '@/lib/components/Account/Settings/DialogWalletStepUp';
import { useTelegramConnect } from '@/lib/hooks/useTelegramConnect';
import type { MarketItem } from '@/lib/types/markets/market';
import { preloadAnimation } from '@/lib/ui-kit/lottie-illustration';
import { cn } from '@/lib/ui-kit/utils';
import { Button } from '@/lib/ui-kit-v2/button';
import { BUTTON_M_CLASS, Z_OVERLAY_MAX } from '../constants';
import { PriceAlertDeliverySection } from '../PriceAlertDeliverySection';
import { TelegramRequiredDialog } from '../TelegramRequiredDialog';
import type {
  PriceAlertCondition,
  PriceAlertExpires,
  PriceAlertRepeat,
  PriceAlertSide,
} from '../types';
import {
  dollarsToCents,
  getDefaultUsdCents,
  isUsdValueCondition,
  useOutcomeLiveCents,
} from '../utils';
import { ConditionField } from './ConditionField';
import { ExpiresField } from './ExpiresField';
import { NoteField } from './NoteField';
import { OutcomeAndSideField } from './OutcomeAndSideField';
import { RepeatField } from './RepeatField';
import { TargetPriceField } from './TargetPriceField';
import { TargetVolumeField } from './TargetVolumeField';

interface PriceAlertsCreateViewProps {
  eventId: string;
  marketTitle: string;
  outcomes: MarketItem[];
  defaultOutcomeId?: string;
  onCancel: () => void;
  onCreate: (payload: {
    outcomeId: string;
    side: PriceAlertSide;
    condition: PriceAlertCondition;
    targetCents: number;
    repeat: PriceAlertRepeat;
    expires: PriceAlertExpires;
    note: string;
  }) => void;
  isSubmitting?: boolean;
}

export function PriceAlertsCreateView({
  eventId,
  marketTitle,
  outcomes,
  defaultOutcomeId,
  onCancel,
  onCreate,
  isSubmitting = false,
}: PriceAlertsCreateViewProps) {
  const t = useTranslations('priceAlerts');
  const tSettings = useTranslations('account.settings');

  const initialOutcome = outcomes.find((o) => o.id === defaultOutcomeId) ?? outcomes[0];

  const [outcomeId, setOutcomeId] = useState(initialOutcome?.id ?? '');
  const [side, setSide] = useState<PriceAlertSide>('NO');
  const [condition, setCondition] = useState<PriceAlertCondition>('PRICE_ABOVE');
  const [note, setNote] = useState('');
  const [repeat, setRepeat] = useState<PriceAlertRepeat>('ONCE');
  const [expires, setExpires] = useState<PriceAlertExpires>('UNTIL_END');
  const [showTelegramRequired, setShowTelegramRequired] = useState(false);

  const updatePreference = useUpdateNotificationPreference();
  const {
    linked: telegramLinked,
    connectViaDeepLink: connectTelegramViaDeepLink,
    isConnecting,
    isStatusLoading,
    stepUp: telegramStepUp,
  } = useTelegramConnect();
  // Set when the user starts the connect flow from the gate — once Telegram is
  // linked (poll flips `linked`) we auto-enable notifications and create the alert.
  const autoCreateRef = useRef(false);

  // The telegram-required gate (server-error illustration) appears on Create
  // when Telegram isn't linked — warm it up front so the gate opens instantly.
  useEffect(() => {
    if (isStatusLoading || telegramLinked) return;
    preloadAnimation('server-error');
  }, [isStatusLoading, telegramLinked]);

  const selectedOutcome = useMemo(
    () => outcomes.find((o) => o.id === outcomeId) ?? initialOutcome,
    [outcomes, outcomeId, initialOutcome],
  );

  const { yesCents: liveYesCents, noCents: liveNoCents } = useOutcomeLiveCents(selectedOutcome);
  const currentCents = side === 'YES' ? liveYesCents : liveNoCents;

  const [targetOverride, setTargetOverride] = useState<number | null>(null);

  const [usdOverrideByCondition, setUsdOverrideByCondition] = useState<Record<string, number>>({});

  const isUsd = isUsdValueCondition(condition);

  const currentValueQuery = usePriceAlertCurrentValue(
    { eventId, outcomeId, side, conditionType: condition },
    { enabled: Boolean(eventId && outcomeId) },
  );
  const isCurrentValueLoading = currentValueQuery.isLoading;
  const serverValue = currentValueQuery.data ?? null;
  const serverCents = dollarsToCents(serverValue);

  const currentUsd = isUsd && serverValue != null ? serverValue : 0;
  const displayCurrentCents = serverCents ?? currentCents;

  const targetCents =
    targetOverride ?? (serverCents != null ? Math.min(Math.max(serverCents, 1), 99) : 0);
  const setTargetCents = (cents: number) => setTargetOverride(cents);

  const usdCents = usdOverrideByCondition[condition] ?? getDefaultUsdCents(condition, serverValue);
  const setUsdCents = (cents: number) => {
    setUsdOverrideByCondition((prev) => ({ ...prev, [condition]: cents }));
  };

  const isValid = isUsd
    ? usdCents > 0 && Boolean(outcomeId)
    : targetCents > 0 && targetCents < 100 && Boolean(outcomeId);

  const submitCreate = useCallback(() => {
    onCreate({
      outcomeId,
      side,
      condition,
      targetCents: isUsd ? usdCents : targetCents,
      repeat,
      expires,
      note,
    });
  }, [onCreate, outcomeId, side, condition, isUsd, usdCents, targetCents, repeat, expires, note]);

  const handleCreate = () => {
    if (!isValid) return;
    // Telegram already connected (status linked) → create straight away.
    if (telegramLinked) {
      submitCreate();
      return;
    }
    // Not connected → open the gate; the bot deep-link / 2FA happen from there.
    setShowTelegramRequired(true);
  };

  // Gate "Connect": go straight to the bot deep-link (the backend decides
  // connect+enable vs enable-only; a 2FA prompt shows only when attaching a new
  // identity with 2FA on). Flag that, once linked, we finish automatically.
  const handleTelegramConfirm = () => {
    autoCreateRef.current = true;
    connectTelegramViaDeepLink();
  };

  // Once the bot Start links Telegram (poll flips `linked`), auto-enable
  // price-alert notifications and create the alert the user was making.
  useEffect(() => {
    if (!autoCreateRef.current || !telegramLinked) return;
    autoCreateRef.current = false;
    updatePreference.mutate(
      { type: 'PRICE_ALERT', telegramEnabled: true },
      {
        onSuccess: () => {
          setShowTelegramRequired(false);
          submitCreate();
        },
      },
    );
  }, [telegramLinked, updatePreference, submitCreate]);

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-6">
      <div
        className={cn(
          'flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto px-1 transition-opacity',
          isSubmitting && 'pointer-events-none opacity-50',
        )}
        aria-busy={isSubmitting || undefined}
      >
        <h3 className={cn(BUTTON_M_CLASS, 'text-v2-text-primary')}>{marketTitle}</h3>

        <OutcomeAndSideField
          outcomes={outcomes}
          outcomeId={outcomeId}
          onOutcomeIdChange={setOutcomeId}
          side={side}
          onSideChange={setSide}
        />

        <ConditionField value={condition} onChange={setCondition} />

        {isUsd ? (
          <TargetVolumeField
            valueCents={usdCents}
            onChangeCents={setUsdCents}
            currentVolume={currentUsd}
            condition={condition}
            isLoading={isCurrentValueLoading}
          />
        ) : (
          <TargetPriceField
            valueCents={targetCents}
            onChangeCents={setTargetCents}
            currentCents={displayCurrentCents}
            isLoading={isCurrentValueLoading}
          />
        )}

        <div className="grid grid-cols-1 gap-4 min-[800px]:grid-cols-2">
          <RepeatField value={repeat} onChange={setRepeat} />
          <ExpiresField value={expires} onChange={setExpires} />
        </div>

        <NoteField value={note} onChange={setNote} />

        <PriceAlertDeliverySection />
      </div>

      <div className="flex gap-4">
        <Button
          type="button"
          appearance="neutral"
          size="M"
          className="flex-1"
          onClick={onCancel}
          disabled={isSubmitting}
        >
          {t('cancel')}
        </Button>
        <Button
          type="button"
          appearance="accent"
          size="M"
          className="flex-1"
          onClick={handleCreate}
          disabled={!isValid || isSubmitting || isStatusLoading || isCurrentValueLoading}
          leftIcon={isSubmitting && <Loader2 className="size-4 animate-spin" />}
        >
          {t('createAlert')}
        </Button>
      </div>

      <TelegramRequiredDialog
        open={showTelegramRequired}
        mode="connect"
        onOpenChange={(open) => {
          // Dismissing the gate cancels the pending auto-create.
          if (!open) autoCreateRef.current = false;
          setShowTelegramRequired(open);
        }}
        onConfirm={handleTelegramConfirm}
        isBusy={isConnecting}
      />

      {/* 2FA step-up — only shown when attaching a new Telegram identity (no
          identity yet) with 2FA on; collects the TOTP before the deep-link opens. */}
      {telegramStepUp.target && (
        <DialogWalletStepUp
          isOpen
          setIsOpen={(open) => {
            if (!open) telegramStepUp.close();
          }}
          title={tSettings('enableTelegramTitle')}
          confirmLabel={tSettings('enableNotifications')}
          require2fa
          isSubmitting={telegramStepUp.isSubmitting}
          hasError={telegramStepUp.hasError}
          onConfirm={telegramStepUp.submit}
          style={{ zIndex: Z_OVERLAY_MAX }}
        />
      )}
    </div>
  );
}
